import React, { useState } from 'react';
import { AlertTriangle, CheckCircle2, Copy, RefreshCw } from 'lucide-react';
import Card from '../common/Card';
import Button from '../common/Button';
import Badge from '../common/Badge';
import { useToast } from '../../contexts/ToastContext';

// Mostrado quando `customDomain` existe e `customDomainVerified === false`.
// Explica o passo DNS + oferece botão "Verificar agora".
const DNSInstructionsCard = ({ store, onVerify, verifying }) => {
  const { success, info } = useToast();
  const [lastVerifyAt, setLastVerifyAt] = useState(null);

  if (!store?.customDomain) return null;

  const verified = !!store.customDomainVerified;
  const token = store.customDomainVerificationToken || 'tok-pendente';
  const recordHost = `_reviter-verify.${store.customDomain}`;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      success(`${label} copiado!`);
    } catch {
      info('Use Ctrl+C pra copiar manualmente.');
    }
  };

  const handleVerify = async () => {
    setLastVerifyAt(Date.now());
    await onVerify?.();
  };

  if (verified) {
    return (
      <Card className="p-4 bg-success/5 border-success/30">
        <div className="flex items-center gap-3">
          <CheckCircle2 size={24} className="text-success flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-app-text">
              Domínio verificado: {store.customDomain}
            </p>
            <p className="text-xs text-app-text-secondary">
              Sua loja está acessível publicamente nesse domínio.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-5 bg-warning/5 border-warning/30">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle size={24} className="text-warning flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-app-text">Domínio pendente de verificação</p>
            <Badge color="warning" size="sm" variant="soft">
              Não verificado
            </Badge>
          </div>
          <p className="text-sm text-app-text-secondary">
            Pra ativar <code className="px-1.5 py-0.5 bg-app-surface-hover rounded font-mono text-xs">{store.customDomain}</code>{' '}
            adicione um registro DNS no seu provedor (Registro.br, GoDaddy, Cloudflare etc):
          </p>
        </div>
      </div>

      <div className="bg-white border border-app-border rounded-lg p-4 mb-4 space-y-3">
        <div>
          <p className="text-xs font-semibold text-app-text-secondary uppercase mb-1">Tipo</p>
          <p className="font-mono text-sm text-app-text">TXT</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <p className="text-xs font-semibold text-app-text-secondary uppercase mb-1">Nome/Host</p>
            <div className="flex items-center gap-2">
              <code className="font-mono text-sm text-app-text bg-app-surface px-2 py-1 rounded flex-1 truncate">
                {recordHost}
              </code>
              <button
                type="button"
                onClick={() => copy(recordHost, 'Host')}
                className="p-1.5 text-app-text-secondary hover:text-primary hover:bg-app-surface-hover rounded transition-colors"
                aria-label="Copiar host"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-app-text-secondary uppercase mb-1">Valor</p>
            <div className="flex items-center gap-2">
              <code className="font-mono text-sm text-app-text bg-app-surface px-2 py-1 rounded flex-1 truncate">
                {token}
              </code>
              <button
                type="button"
                onClick={() => copy(token, 'Token')}
                className="p-1.5 text-app-text-secondary hover:text-primary hover:bg-app-surface-hover rounded transition-colors"
                aria-label="Copiar token"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-xs text-app-text-secondary">
          A propagação DNS pode levar até alguns minutos.
        </p>
        <Button
          size="sm"
          onClick={handleVerify}
          loading={verifying}
          leftIcon={<RefreshCw size={14} />}
        >
          Verificar agora
        </Button>
      </div>
      {lastVerifyAt && !verifying && (
        <p className="text-xs text-app-text-secondary mt-2">
          Última tentativa: {new Date(lastVerifyAt).toLocaleTimeString('pt-BR')}
        </p>
      )}
    </Card>
  );
};

export default DNSInstructionsCard;
